import {useState, useEffect, useCallback} from 'react';
import _ from 'lodash';
import {Table, Tag, Input, Select, Form, Button, Avatar} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import moment from 'moment';
import {GET_DABA_POWER_USER_DEPOSITS} from '../resolvers';
import styles from './dabaPower.module.scss';
import UserLink from 'components/userLink/UserLink';
import {currencySymbols} from 'utils/mock';

const CURRENCY_OPTIONS = [
  {value: '', label: 'All Currencies'},
  {value: 'XOF', label: 'XOF'},
  {value: 'USD', label: 'USD'},
  {value: 'EUR', label: 'EUR'},
  {value: 'XAF', label: 'XAF'},
];

export const DepositsTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currency, setCurrency] = useState('');
  const [debouncedCurrency, setDebouncedCurrency] = useState('');
  const [minDeposits, setMinDeposits] = useState(0);
  const [debouncedMinDeposits, setDebouncedMinDeposits] = useState(0);

  const debouncedSetSearch = useCallback(
    _.debounce(value => setDebouncedSearch(value), 400),
    [],
  );
  const debouncedSetCurrency = useCallback(
    _.debounce(value => setDebouncedCurrency(value), 400),
    [],
  );
  const debouncedSetMinDeposits = useCallback(
    _.debounce(value => setDebouncedMinDeposits(value), 400),
    [],
  );

  useEffect(() => {
    debouncedSetSearch(search);
    return () => debouncedSetSearch.cancel?.();
  }, [search]);
  useEffect(() => {
    debouncedSetCurrency(currency);
    return () => debouncedSetCurrency.cancel?.();
  }, [currency]);
  useEffect(() => {
    debouncedSetMinDeposits(minDeposits);
    return () => debouncedSetMinDeposits.cancel?.();
  }, [minDeposits]);

  const {data, loading, refetch} = useQuery(GET_DABA_POWER_USER_DEPOSITS, {
    variables: {
      filters: {
        search: debouncedSearch || undefined,
        currency: debouncedCurrency || undefined,
        minDeposits:
          debouncedMinDeposits > 0 ? debouncedMinDeposits : undefined,
        paginate: true,
        page: currentPage,
        limit,
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error?.message),
  });

  useEffect(() => {
    setCurrentPage(1);
    refetch({
      filters: {
        search: debouncedSearch || undefined,
        currency: debouncedCurrency || undefined,
        minDeposits:
          debouncedMinDeposits > 0 ? debouncedMinDeposits : undefined,
        paginate: true,
        page: 1,
        limit,
      },
    });
  }, [debouncedSearch, debouncedCurrency, debouncedMinDeposits]);

  const handleReset = () => {
    setSearch('');
    setDebouncedSearch('');
    setCurrency('');
    setDebouncedCurrency('');
    setMinDeposits(0);
    setDebouncedMinDeposits(0);
    setCurrentPage(1);
    debouncedSetSearch.cancel?.();
    debouncedSetCurrency.cancel?.();
    debouncedSetMinDeposits.cancel?.();
    refetch({
      filters: {
        paginate: true,
        page: 1,
        limit,
      },
    });
  };

  const handleTableChange = pagination => {
    setCurrentPage(pagination.current);
    setLimit(pagination.pageSize);
    refetch({
      filters: {
        search: debouncedSearch || undefined,
        currency: debouncedCurrency || undefined,
        minDeposits:
          debouncedMinDeposits > 0 ? debouncedMinDeposits : undefined,
        paginate: true,
        page: pagination.current,
        limit: pagination.pageSize,
      },
    });
  };

  const response = data?.getDabaPowerUserDeposits;
  const dataSource =
    response?.__typename === 'ResponseWithDabaPowerUserDeposits'
      ? response?.data || []
      : [];
  const paginationData = response?.pagination || {};

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const name = [record?.user?.firstName, record?.user?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        return (
          <div className={styles.userCell}>
            <Avatar src={record?.user?.imageUrl} icon={<UserOutlined />} />
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span className={styles.userName}>
                <UserLink userId={record?.user?.id}>
                  {name || record?.user?.email || record?.user?.id || '—'}
                </UserLink>
              </span>
              <span className={styles.userEmail}>
                {record?.user?.email || '—'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Location',
      key: 'location',
      dataIndex: 'user',
      render: val =>
        val?.country
          ? `${val?.city ? `${val?.city}, ` : ''}${val?.country}`.trim()
          : '—',
    },
    {
      title: 'Daba Pro Status',
      dataIndex: 'isProUser',
      key: 'isProUser',
      render: isPro => (
        <Tag color={isPro ? 'purple' : 'default'}>{isPro ? 'Pro' : 'Free'}</Tag>
      ),
    },
    {
      title: 'Total Deposits',
      dataIndex: 'totalDeposits',
      key: 'totalDeposits',
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (val, record) =>
        val != null
          ? `${currencySymbols[record?.currency]?.symbol || ''} ${Number(
              val,
            ).toLocaleString()}`.trim()
          : '—',
    },
    {
      title: 'Last Deposit',
      dataIndex: 'lastDeposit',
      key: 'lastDeposit',
      render: date =>
        date ? moment(date).format('DD MMM, YYYY hh:mm a') : '—',
    },
    {
      title: 'Joined Date',
      dataIndex: 'user',
      key: 'joinedDate',
      render: date =>
        date ? moment(date?.createdAt).format('DD MMM, YYYY hh:mm a') : '—',
    },
  ];

  return (
    <div className={styles.membersTableContainer}>
      <div className={styles.membersHeader}>
        <h3 className={styles.sectionTitle}>Daba Power Users - Deposits</h3>
      </div>
      <div className={styles.filterSection}>
        <p className={styles.filterDescription}>
          Filter users by number of successful deposits.
        </p>
        <div className={styles.filterRow}>
          <Form.Item className={styles.filterField}>
            <Input
              placeholder="Search by username..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={styles.searchInput}
              allowClear
            />
          </Form.Item>
          <Form.Item label="Currency" className={styles.filterField}>
            <Select
              value={currency}
              onChange={setCurrency}
              className={styles.currencySelect}
              options={CURRENCY_OPTIONS}
            />
          </Form.Item>
          <Form.Item label="Min. Deposits" className={styles.filterField}>
            <Input
              type="number"
              min={0}
              value={minDeposits}
              onChange={e => setMinDeposits(Number(e.target.value) || 0)}
              placeholder="0"
              className={styles.numberInput}
            />
          </Form.Item>
          <Button className={styles.resetButton} onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
      <div className={styles.paginationRow}>
        Showing {dataSource.length} of {paginationData.total ?? 0} users
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey={record => record?.user?.id || Math.random()}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: paginationData.total || 0,
          showSizeChanger: true,
          position: ['bottomRight'],
        }}
        onChange={handleTableChange}
        className="antd-table"
      />
    </div>
  );
};
